import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlarmClock, CalendarClock, CheckSquare, CircleCheck, Contact, Coffee, Flame, Gauge, Handshake, Hourglass, Timer, TrendingUp, UserCheck, Users, BadgeCheck } from 'lucide-react';
import { formatDuration } from '@stoplord/shared';
import { dashboardApi } from '@/services/admin';
import { orgApi } from '@/services/hrm';
import { useAuth } from '@/store/auth';
import { useCan, useTerms } from '@/hooks/usePermission';
import { formatDate, formatMoney, formatTime, humanize, relativeTime } from '@/lib/format';
import { Badge, Card, CardBody, CardHeader, EmptyState, ErrorState, PageHeader, PRIORITY_TONE, Select, Skeleton, StatCard, Tabs, TASK_STATUS_TONE, TEMPERATURE_TONE } from '@/components/ui';
import { ChartCard, RankedBars } from '@/components/charts';

/* eslint-disable @typescript-eslint/no-explicit-any */
type Overview = any;

const RANGES = [
  { key: 'month', label: 'This month' },
  { key: '30d', label: 'Last 30 days' },
  { key: '90d', label: 'Last 90 days' },
  { key: 'year', label: 'This year' },
] as const;

function rangeDates(key: string): { from?: string; to?: string } {
  const now = new Date();
  if (key === '30d') return { from: new Date(now.getTime() - 30 * 86_400_000).toISOString() };
  if (key === '90d') return { from: new Date(now.getTime() - 90 * 86_400_000).toISOString() };
  if (key === 'year') return { from: new Date(now.getFullYear(), 0, 1).toISOString() };
  return {};
}

const monthLabel = (m: string) => new Date(`${m}-01T00:00:00`).toLocaleDateString(undefined, { month: 'short' });
const dayLabel = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

export function DashboardPage() {
  const can = useCan();
  const tracks = useAuth((s) => !!s.user?.tracksWorkTime);
  const canOverview = can('dashboard.company');
  const [tab, setTab] = useState<'overview' | 'me'>(canOverview ? 'overview' : 'me');
  return (
    <>
      {canOverview && tracks && (
        <Tabs className="mb-5" value={tab} onChange={setTab} items={[{ value: 'overview', label: 'Business overview' }, { value: 'me', label: 'My day' }]} />
      )}
      {canOverview && tab === 'overview' ? <Overview /> : <MyDashboard />}
    </>
  );
}

function Overview() {
  const terms = useTerms();
  const tenant = useAuth((s) => s.tenant);
  const [range, setRange] = useState<string>('month');
  const [departmentId, setDepartmentId] = useState('');
  const [branchId, setBranchId] = useState('');
  const can = useCan();
  const org = useQuery({ queryKey: ['org'], queryFn: orgApi.get, enabled: can('hrm.org.view') });
  const params = useMemo(() => ({ ...rangeDates(range), departmentId: departmentId || undefined, branchId: branchId || undefined }), [range, departmentId, branchId]);
  const q = useQuery({ queryKey: ['dashboard', 'overview', params], queryFn: () => dashboardApi.overview(params) as Promise<Overview>, refetchInterval: 60_000 });
  const d: Overview = q.data;
  const loading = q.isLoading;
  const rangeLabel = RANGES.find((r) => r.key === range)!.label.toLowerCase();

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle={`${tenant?.name ?? ''} · live figures from your workspace`}
        actions={
          <div className="flex flex-wrap gap-2">
            <Select aria-label="Date range" value={range} onChange={(e) => setRange(e.target.value)} className="h-9 w-auto">
              {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </Select>
            {org.data && (
              <>
                <Select aria-label="Branch" value={branchId} onChange={(e) => { setBranchId(e.target.value); setDepartmentId(''); }} className="h-9 w-auto">
                  <option value="">All branches</option>
                  {org.data.branches.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
                </Select>
                <Select aria-label="Department" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} className="h-9 w-auto">
                  <option value="">All departments</option>
                  {org.data.departments.filter((x) => !branchId || x.branchId === branchId).map((x) => <option key={x._id} value={x._id}>{x.name}</option>)}
                </Select>
              </>
            )}
          </div>
        }
      />
      {q.error ? (
        <Card><ErrorState error={q.error} onRetry={() => q.refetch()} /></Card>
      ) : (
        <div className="space-y-5">
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
            {(loading || d?.leads) && <StatCard loading={loading} label={`Open ${terms.lead.plural.toLowerCase()}`} value={d?.leads?.open ?? 0} icon={<Contact className="h-4 w-4" />} hint={`${d?.leads?.createdInRange ?? 0} new ${rangeLabel}`} to="/crm/leads?status=open" />}
            {(loading || d?.leads) && <StatCard loading={loading} label="Conversion rate" value={d?.leads?.conversionRate === null || d?.leads?.conversionRate === undefined ? '—' : `${d.leads.conversionRate}%`} icon={<TrendingUp className="h-4 w-4" />} hint={`${d?.leads?.wonInRange ?? 0} won · ${d?.leads?.lostInRange ?? 0} lost ${rangeLabel}`} />}
            {(loading || d?.clients) && <StatCard loading={loading} label={`Active ${terms.client.plural.toLowerCase()}`} value={d?.clients?.active ?? 0} icon={<Handshake className="h-4 w-4" />} hint={`${d?.clients?.newInRange ?? 0} added ${rangeLabel}`} to="/crm/clients" />}
            {(loading || d?.tasks) && <StatCard loading={loading} label={`Open ${terms.task.plural.toLowerCase()}`} value={d?.tasks?.open ?? 0} icon={<CheckSquare className="h-4 w-4" />} tone={d?.tasks?.overdue ? 'warning' : 'brand'} hint={`${d?.tasks?.overdue ?? 0} overdue · ${d?.tasks?.dueToday ?? 0} due today`} to="/tasks?view=all&status=open" />}
          </div>

          <div className="grid gap-5 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
            {/* Pipeline breakdown (like a receivables summary) */}
            {(loading || d?.leads) && (
              <Card>
                <CardHeader title="Pipeline by stage" subtitle="Open leads and their estimated value" action={<span className="tabular text-sm font-semibold text-ink">{loading ? '' : formatMoney(d?.leads?.pipelineValue, { compact: true })}</span>} />
                <CardBody>
                  {loading ? <Skeleton className="h-40" /> : (
                    <ul className="space-y-2.5">
                      {d.leads.byStage.map((s: any) => (
                        <li key={s.key} className="flex items-center gap-3 text-sm">
                          <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: s.color }} aria-hidden />
                          <Link to={`/crm/leads?stage=${s.key}&status=open`} className="shrink-0 text-ink hover:underline">{s.label}</Link>
                          <span className="min-w-4 flex-1 border-b border-dashed border-line" aria-hidden />
                          <span className="tabular shrink-0 text-ink-muted">{s.count}</span>
                          <span className="tabular w-20 shrink-0 text-right text-ink">{formatMoney(s.value, { compact: true })}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardBody>
              </Card>
            )}
            {/* Operational tiles */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {(loading || d?.leads) && <MiniStat icon={<CalendarClock className="h-4 w-4" />} label="Follow-ups today" value={d?.leads?.followupsToday} to="/crm/followups?due=today" loading={loading} />}
              {(loading || d?.leads) && <MiniStat icon={<AlarmClock className="h-4 w-4" />} label="Overdue follow-ups" value={d?.leads?.followupsOverdue} to="/crm/followups?due=overdue" loading={loading} warn={!!d?.leads?.followupsOverdue} />}
              {(loading || d?.tasks) && <MiniStat icon={<Hourglass className="h-4 w-4" />} label="Awaiting approval" value={d?.tasks?.inReview} to="/tasks?view=all&status=in_review" loading={loading} />}
              {(loading || d?.tasks) && <MiniStat icon={<CircleCheck className="h-4 w-4" />} label={`Completed ${rangeLabel}`} value={d?.tasks?.completedInRange} loading={loading} />}
              {(loading || d?.people) && <MiniStat icon={<UserCheck className="h-4 w-4" />} label="Present today" value={d?.people ? `${d.people.presentToday}/${d.people.withLogin}` : undefined} to="/hrm/work-monitor" loading={loading} />}
              {(loading || d?.people) && <MiniStat icon={<Gauge className="h-4 w-4" />} label="Working now" value={d?.people ? d.people.activeNow : undefined} sub={d?.people ? `${d.people.idleNow} idle · ${d.people.onBreakNow} on break` : undefined} to="/hrm/work-monitor" loading={loading} />}
            </div>
          </div>

          <div className="grid gap-5 lg:grid-cols-3">
            {d?.leads && (
              <div className="lg:col-span-2">
                <ChartCard title={`${terms.lead.plural} per month`} subtitle="Created vs won, last 6 months" data={d.leads.monthly} xKey="month" xFormat={monthLabel} series={[{ key: 'created', label: 'Created' }, { key: 'won', label: 'Won' }]} />
              </div>
            )}
            {d?.leads && (
              <Card>
                <CardHeader title={`${terms.lead.singular} sources`} subtitle={`New ${terms.lead.plural.toLowerCase()} ${rangeLabel}`} />
                <CardBody><RankedBars items={d.leads.bySource.map((s: any) => ({ label: s.label, value: s.count, sub: s.won ? `· ${s.won} won` : undefined }))} /></CardBody>
              </Card>
            )}
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            {d?.people && <ChartCard kind="line" title="Attendance" subtitle="People with a working session, last 14 days" data={d.people.attendanceTrend} xKey="date" xFormat={dayLabel} series={[{ key: 'present', label: 'Present' }]} />}
            {d?.tasks && <ChartCard title={`${terms.task.singular} flow`} subtitle="Created vs completed per week, last 8 weeks" data={d.tasks.weekly} xKey="week" xFormat={dayLabel} series={[{ key: 'created', label: 'Created' }, { key: 'completed', label: 'Completed' }]} />}
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            {d?.leads && (
              <Card>
                <CardHeader title={`Latest ${terms.lead.plural.toLowerCase()}`} action={<Link to="/crm/leads" className="text-xs font-medium text-brand-ink hover:underline">View all</Link>} />
                <CardBody className="pt-1">
                  {d.leads.latest.length === 0 ? <EmptyState title={`No ${terms.lead.plural.toLowerCase()} yet`} className="py-6" /> : (
                    <ul className="divide-y divide-line">
                      {d.leads.latest.map((l: any) => (
                        <li key={l._id}>
                          <Link to={`/crm/leads/${l._id}`} className="flex items-center gap-3 py-2.5 hover:opacity-80">
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-ink">{l.name}{l.company && <span className="font-normal text-ink-muted"> · {l.company}</span>}</p>
                              <p className="text-xs text-ink-subtle">{l.leadNumber} · {l.owner?.name ?? 'Unassigned'} · {relativeTime(l.createdAt)}</p>
                            </div>
                            <Badge tone={TEMPERATURE_TONE[l.temperature]}>{humanize(l.temperature)}</Badge>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardBody>
              </Card>
            )}
            {d?.tasks && (
              <Card>
                <CardHeader title="Upcoming deadlines" subtitle="Open tasks due in the next 7 days" action={<Link to="/tasks?view=all&due=upcoming" className="text-xs font-medium text-brand-ink hover:underline">View all</Link>} />
                <CardBody className="pt-1">
                  {d.tasks.upcoming.length === 0 ? <EmptyState title="Nothing due this week" className="py-6" /> : (
                    <ul className="divide-y divide-line">
                      {d.tasks.upcoming.map((t: any) => (
                        <li key={t._id}>
                          <Link to={`/tasks/${t._id}`} className="flex items-center gap-3 py-2.5 hover:opacity-80">
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-ink">{t.title}</p>
                              <p className="truncate text-xs text-ink-subtle">{t.taskNumber} · {t.assignees.map((a: any) => a.name).join(', ') || 'Unassigned'}</p>
                            </div>
                            <div className="text-right">
                              <Badge tone={PRIORITY_TONE[t.priority]}>{humanize(t.priority)}</Badge>
                              <p className="mt-1 text-xs text-ink-subtle">{formatDate(t.dueAt)}</p>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardBody>
              </Card>
            )}
          </div>
          {d && (
            <p className="text-xs text-ink-subtle">
              Accounting, projects, quotations, support and recruitment figures will appear here once those modules are enabled. Updated {formatTime(d.generatedAt)}.
            </p>
          )}
        </div>
      )}
    </>
  );
}

function MiniStat({ icon, label, value, sub, to, loading, warn }: { icon: React.ReactNode; label: string; value?: React.ReactNode; sub?: string; to?: string; loading?: boolean; warn?: boolean }) {
  const body = (
    <Card className="flex h-full items-start gap-3 p-4 transition-shadow hover:shadow-md">
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${warn ? 'bg-warning-soft text-warning' : 'bg-brand-soft text-brand-ink'}`} aria-hidden>{icon}</span>
      <div className="min-w-0">
        <p className="text-xs text-ink-muted">{label}</p>
        {loading ? <Skeleton className="mt-1 h-5 w-10" /> : <p className="tabular text-lg font-semibold text-ink">{value ?? 0}</p>}
        {sub && <p className="text-[11px] text-ink-subtle">{sub}</p>}
      </div>
    </Card>
  );
  return to ? <Link to={to} className="block rounded-xl">{body}</Link> : body;
}

function MyDashboard() {
  const user = useAuth((s) => s.user);
  const ws = useAuth((s) => s.workSession);
  const terms = useTerms();
  const q = useQuery({ queryKey: ['dashboard', 'me'], queryFn: () => dashboardApi.me() as Promise<any>, refetchInterval: 60_000 });
  const d = q.data;
  const tz = useAuth((s) => s.tenant?.timezone);
  // Greet in the workspace's time zone, not the device's.
  const hour = Number(new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hourCycle: 'h23', timeZone: tz }).format(new Date()));
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const today = d?.workTime?.today;
  return (
    <>
      <PageHeader title={`${greeting}, ${user?.name.split(' ')[0] ?? ''}`} subtitle={ws && ws.state !== 'ended' ? `Signed in at ${formatTime(ws.loginAt)} · ${humanize(ws.state)}` : 'Here is what needs your attention today.'} />
      {q.error ? <Card><ErrorState error={q.error} onRetry={() => q.refetch()} /></Card> : (
        <div className="space-y-5">
          {d?.workTime && (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <StatCard label="Active today" value={formatDuration(today?.activeMs ?? 0)} icon={<Timer className="h-4 w-4" />} hint="Time you were interacting with StopLord" />
              <StatCard label="Idle today" value={formatDuration(today?.idleMs ?? 0)} icon={<Hourglass className="h-4 w-4" />} tone="warning" hint="Signed in without activity" />
              <StatCard label="Breaks today" value={formatDuration(today?.breakMs ?? 0)} icon={<Coffee className="h-4 w-4" />} tone="info" />
              <StatCard label="First sign-in" value={today?.firstLoginAt ? formatTime(today.firstLoginAt) : '—'} icon={<UserCheck className="h-4 w-4" />} tone="success" />
            </div>
          )}
          {d?.tasks && (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <StatCard loading={q.isLoading} label={`My open ${terms.task.plural.toLowerCase()}`} value={d.tasks.open} icon={<CheckSquare className="h-4 w-4" />} to="/tasks?view=my&status=open" />
              <StatCard loading={q.isLoading} label="Due today" value={d.tasks.dueToday} icon={<CalendarClock className="h-4 w-4" />} tone="info" to="/tasks?view=my&due=today" />
              <StatCard loading={q.isLoading} label="Overdue" value={d.tasks.overdue} icon={<AlarmClock className="h-4 w-4" />} tone={d.tasks.overdue ? 'danger' : 'brand'} to="/tasks?view=my&due=overdue" />
              <StatCard loading={q.isLoading} label="Completed this week" value={d.tasks.completedWeek} icon={<CircleCheck className="h-4 w-4" />} tone="success" />
            </div>
          )}
          <div className="grid gap-5 lg:grid-cols-2">
            {d?.tasks && (
              <Card>
                <CardHeader title={`My next ${terms.task.plural.toLowerCase()}`} action={<Link to="/tasks" className="text-xs font-medium text-brand-ink hover:underline">Open tasks</Link>} />
                <CardBody className="pt-1">
                  {d.tasks.next.length === 0 ? <EmptyState title="No open tasks — nice work." className="py-6" /> : (
                    <ul className="divide-y divide-line">
                      {d.tasks.next.map((t: any) => (
                        <li key={t._id}>
                          <Link to={`/tasks/${t._id}`} className="flex items-center justify-between gap-3 py-2.5 hover:opacity-80">
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium text-ink">{t.title}</span>
                              <span className="text-xs text-ink-subtle">{t.taskNumber} · {t.dueAt ? `due ${formatDate(t.dueAt)}` : 'no deadline'}</span>
                            </span>
                            <Badge tone={TASK_STATUS_TONE[t.status]}>{humanize(t.status)}</Badge>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardBody>
              </Card>
            )}
            {d?.leads && (
              <Card>
                <CardHeader title="My follow-ups" subtitle={`${d.leads.followupsToday} today · ${d.leads.followupsOverdue} overdue · ${d.leads.hot} hot ${terms.lead.plural.toLowerCase()}`} action={<Link to="/crm/followups" className="text-xs font-medium text-brand-ink hover:underline">All follow-ups</Link>} />
                <CardBody className="pt-1">
                  {d.leads.nextFollowups.length === 0 ? <EmptyState title="No follow-ups scheduled" className="py-6" /> : (
                    <ul className="divide-y divide-line">
                      {d.leads.nextFollowups.map((f: any) => (
                        <li key={f._id}>
                          <Link to={`/crm/leads/${f.leadId}`} className="flex items-center justify-between gap-3 py-2.5 hover:opacity-80">
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium text-ink">{f.lead?.name ?? 'Lead'}{f.lead?.company && <span className="font-normal text-ink-muted"> · {f.lead.company}</span>}</span>
                              <span className="text-xs text-ink-subtle">{humanize(f.channel)} · {f.lead?.phone ?? ''}</span>
                            </span>
                            <span className={`tabular shrink-0 text-xs ${new Date(f.dueAt) < new Date() ? 'font-medium text-danger' : 'text-ink-muted'}`}>{relativeTime(f.dueAt)}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardBody>
              </Card>
            )}
          </div>
          {d?.leads && (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <StatCard label={`My open ${terms.lead.plural.toLowerCase()}`} value={d.leads.open} icon={<Contact className="h-4 w-4" />} to="/crm/leads?ownerId=me&status=open" />
              <StatCard label="Hot" value={d.leads.hot} icon={<Flame className="h-4 w-4" />} tone="danger" />
              <StatCard label="New this month" value={d.leads.newThisMonth} icon={<Users className="h-4 w-4" />} tone="info" />
              <StatCard label="Converted this month" value={d.leads.wonThisMonth} icon={<BadgeCheck className="h-4 w-4" />} tone="success" />
            </div>
          )}
          {d?.workTime && (
            <ChartCard title="My active hours" subtitle="Last 7 days" data={d.workTime.week.map((w: any) => ({ date: w.date, active: Math.round((w.activeMs / 3_600_000) * 10) / 10 }))} xKey="date" xFormat={dayLabel} series={[{ key: 'active', label: 'Active hours' }]} />
          )}
        </div>
      )}
    </>
  );
}
